import { Link } from '@tanstack/react-router';
import { Code2, FileText, Play, ChevronRight } from 'lucide-react';

import { CATEGORY_META, CATEGORY_ORDER, categorySubtitle, dueLabel, partitionByGroup } from './practiceMeta';
import { PracticeGroupCard } from './PracticeGroupCard';
import { PracticeItemLink } from './PracticeItemLink';
import { PracticeRow } from './PracticeRow';

import type { PracticeItem } from '@/lib/api/lms';
import { usePractice } from '@/lib/hooks/useLms';

const CARD = 'rounded-[20px] border border-[#ececec] bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]';
const GRADIENT = 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)';

export function PracticeHub({ batchId }: { batchId: string }) {
  const { data, isLoading, isError, refetch } = usePractice(batchId);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#8b5cf6] border-t-transparent" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[1150px] p-4 sm:p-6">
        <div className={`${CARD} p-6 text-center`}>
          <p className="text-[#9ca3af]">Couldn&apos;t load practice for this batch.</p>
          <button onClick={() => refetch()} className="mt-3 rounded-[14px] bg-[#8b5cf6] px-4 py-2 text-sm font-semibold text-white">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const pct = data.total_items > 0 ? Math.round((data.completed_items / data.total_items) * 100) : 0;
  const next: PracticeItem | undefined = data.pending[0];
  const nextDue = next ? dueLabel(next.due_date) : null;
  // Outstanding work, with authored sets collapsed. Ten problems from one week
  // are one card here, not ten rows that crowd out everything else.
  const { grouped: pendingGroups, loose: pendingLoose } = partitionByGroup(
    data.pending,
    data.groups ?? [],
    data.items,
  );
  const groupPreview = pendingGroups.slice(0, 2);
  const loosePreview = pendingLoose.slice(0, 3);
  const hasMorePending =
    pendingGroups.length > groupPreview.length || pendingLoose.length > loosePreview.length;

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-8 px-4 py-6 sm:px-10 sm:py-8">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-[#9ca3af]" aria-label="Breadcrumb">
        <Link to="/courses" className="transition-colors hover:text-[#ff5a1f]">My Batches</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId" params={{ batchId }} className="text-[#ff5a1f] transition-colors hover:opacity-80">{data.batch_name}</Link>
        <span aria-hidden>/</span>
        <span className="font-semibold text-[#111827]">Practice</span>
      </nav>

      {/* Hero */}
      <header className="flex items-center gap-5">
        <span
          className="flex size-16 shrink-0 items-center justify-center rounded-[20px] sm:size-[72px]"
          style={{ background: 'linear-gradient(135deg, #ddd6fe 0%, #ede9fe 100%)' }}
        >
          <Code2 className="size-8 text-[#7c3aed]" />
        </span>
        <div>
          <h1 className="text-[34px] font-bold leading-[1.05] tracking-tight text-[#111827] sm:text-[44px]">Practice</h1>
          <p className="mt-1 text-[15px] text-[#6b7280] sm:text-base">Practice consistently. Master concepts. Build confidence.</p>
        </div>
      </header>

      {/* Continue card */}
      {next && (
        <PracticeItemLink item={next} batchId={batchId} className={`${CARD} flex flex-col gap-4 p-5 transition-shadow hover:shadow-[0px_8px_24px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:gap-5 sm:p-6`}>
          <span
            className="flex size-14 shrink-0 items-center justify-center rounded-[16px] sm:size-16"
            style={{ background: 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)' }}
          >
            <FileText className="size-7 text-[#8b5cf6]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-[#9ca3af]">Continue where you left off</p>
            <p className="mt-0.5 truncate text-[20px] font-bold text-[#111827] sm:text-[22px]">{next.title}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]">
              <span className="font-medium text-[#6b7280]">{pct}% Completed</span>
              <span className="h-2 w-[140px] overflow-hidden rounded-full bg-[#ede9fe]">
                <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: GRADIENT }} />
              </span>
              {nextDue && (
                <>
                  <span aria-hidden className="text-[#d1d5db]">•</span>
                  <span className={`font-semibold ${nextDue.urgent ? 'text-[#ff5a1f]' : 'text-[#9ca3af]'}`}>{nextDue.text}</span>
                </>
              )}
            </div>
          </div>
          <span
            className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-[14px] px-6 text-sm font-semibold text-white shadow-[0px_8px_18px_rgba(139,92,246,0.25)]"
            style={{ background: GRADIENT }}
          >
            <Play className="size-4 fill-white" />
            Continue
          </span>
        </PracticeItemLink>
      )}

      {/* Categories */}
      <section className="space-y-4">
        <h2 className="text-[20px] font-bold text-[#111827]">Practice Categories</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORY_ORDER.map((key) => {
            const meta = CATEGORY_META[key];
            const cat = data.categories.find((c) => c.key === key);
            const Icon = meta.icon;
            const subtitle = categorySubtitle(key, cat?.total ?? 0, cat?.completed ?? 0);
            return (
              <Link
                key={key}
                to="/courses/$batchId/practice/$category"
                params={{ batchId, category: key }}
                className={`${CARD} flex items-center gap-3 p-4 transition-shadow hover:shadow-[0px_8px_24px_rgba(0,0,0,0.06)]`}
              >
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl" style={{ background: meta.bg }}>
                  <Icon className="size-5" style={{ color: meta.fg }} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold text-[#111827]">{meta.label}</p>
                  <p className="truncate text-[13px] font-semibold" style={{ color: meta.fg }}>{subtitle}</p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-[#d1d5db]" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* Pending Work */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] font-bold text-[#111827]">Pending Work</h2>
          {hasMorePending && (
            <Link
              to="/courses/$batchId/practice/$category"
              params={{ batchId, category: 'pending' }}
              className="inline-flex items-center gap-1 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#374151] transition-colors hover:bg-[#f9fafb]"
            >
              View All <ChevronRight className="size-3.5" />
            </Link>
          )}
        </div>
        {data.pending.length > 0 ? (
          <div className="space-y-4">
            {groupPreview.map((g) => (
              <PracticeGroupCard key={g.group.key} grouped={g} batchId={batchId} />
            ))}
            {loosePreview.length > 0 && (
              <div className={`${CARD} divide-y divide-[#f3f4f6] overflow-hidden`}>
                {loosePreview.map((item) => (
                  <PracticeRow key={item.id} item={item} batchId={batchId} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={`${CARD} p-6 text-center text-[14px] text-[#9ca3af]`}>
            You&apos;re all caught up — no pending practice work. 🎉
          </div>
        )}
      </section>
    </div>
  );
}
